import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Chip } from "@/components/sync/EndpointCard";
import { utc } from "@/components/sync/syncText";
import { toast } from "sonner";

const td = "px-2 py-1 border-t border-[var(--border-subtle)] align-top";
const SEV = { error: "text-rose-300", warning: "text-amber-300", info: "text-sky-300" };

export function AlertsList({ data }) {
  const qc = useQueryClient();
  const ack = async (id) => {
    try {
      await api.post(`/monitor/alerts/${id}/ack`);
      qc.invalidateQueries({ queryKey: ["mon-alerts"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data.active.length) return <div data-testid="mon-alerts-none" className="text-[11px] text-emerald-300">No active alerts.</div>;
  return (
    <ul data-testid="mon-alerts" className="space-y-1">
      {data.active.map((a) => (
        <li key={a.id} data-testid={`mon-alert-${a.code}`} className={`flex flex-wrap items-center gap-2 text-[11px] ${SEV[a.severity]}`}>
          <span>{a.message}</span>
          <span className="text-slate-500">first {utc(a.first_seen)} · seen {a.occurrences}×</span>
          {a.acknowledged ? <span className="text-slate-500">acknowledged</span>
            : <button data-testid={`mon-ack-${a.code}`} onClick={() => ack(a.id)} className="underline underline-offset-2 text-slate-300 hover:text-white">Acknowledge</button>}
        </li>
      ))}
    </ul>
  );
}

export function FreshnessGrid({ f }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px] text-slate-400">
      {Object.entries(f.kinds).map(([k, v]) => (
        <div key={k} data-testid={`mon-fresh-${k}`} className="rounded-md border border-[var(--border-subtle)] p-3 space-y-0.5">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-200 capitalize">{k}<Chip status={v.freshness.state} testId={`mon-fresh-${k}-state`} /></div>
          <div>Last attempted: {utc(v.last_attempted_at)} ({v.last_attempt_status || "—"})</div>
          <div>Last successful capture (Tradify): {utc(v.last_successful_capture_at)}</div>
          <div data-testid={`mon-fresh-${k}-broker-ts`}>Broker timestamp: {v.broker_timestamp_supplied ? v.broker_timestamp : "not supplied by broker"}</div>
          <div className="font-mono truncate">Run: {v.last_successful_run_id || "—"}</div>
        </div>
      ))}
      <div className="md:col-span-3 text-[10px] text-slate-500">Thresholds: fresh &lt; {f.thresholds.fresh_below_minutes} min · aging &lt; {f.thresholds.stale_from_hours} h · stale ≥ {f.thresholds.stale_from_hours} h, measured from {f.basis}.</div>
    </div>
  );
}

export function CertList({ c }) {
  return (
    <div className="space-y-1 text-[11px] text-slate-400">
      {c.certificates.map((x) => (
        <div key={x.key} data-testid={`mon-cert-${x.key}`} className="flex flex-wrap items-center gap-2">
          <span className="text-slate-200">{x.name}</span><Chip status={x.status} testId={`mon-cert-${x.key}-status`} />
          <span>{x.not_after ? `${x.not_after.slice(0, 10)} · ${x.days_left} days` : "expiry unknown"}</span>
          <span data-testid={`mon-cert-${x.key}-verification`} className={`font-semibold ${x.verification === "VERIFIED" ? "text-emerald-300" : "text-amber-300"}`}>{x.verification}</span>
          <span data-testid={`mon-cert-${x.key}-source`} className="text-slate-500">{x.verification === "VERIFIED" ? `from certificate metadata (${x.source})` : x.verification === "RECORDED ONLY" ? "owner-recorded date, not yet checked against the installed certificate" : "expiry metadata could not be read"}</span>
        </div>
      ))}
      <div className="text-[10px] text-slate-500">Read-only monitoring. Certificates are never renewed or replaced automatically.</div>
    </div>
  );
}

function EpCell({ e }) {
  return <td className={td}><div>{e.status || "—"}{e.items != null ? ` · ${e.items}` : ""}{e.rejected ? ` · ${e.rejected} rejected` : ""}</div>{e.error && <div className="text-amber-300">{e.error}</div>}<div className="text-slate-500">{e.freshness}</div></td>;
}

export function HistoryTable() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [desc, setDesc] = useState(true);
  const [page, setPage] = useState(1);
  const { data: d } = useQuery({ queryKey: ["mon-history", q, status, desc, page], queryFn: async () => (await api.get("/monitor/history", { params: { q, status: status || undefined, desc, page, page_size: 20 } })).data });
  const pages = d ? Math.max(1, Math.ceil(d.total / d.page_size)) : 1;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="mon-history-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search run ID or error" className="h-7 w-48 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
        <select data-testid="mon-history-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="h-7 text-xs bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded">
          <option value="">All statuses</option>{["completed", "partial", "failed", "abandoned", "running"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button data-testid="mon-history-sort" onClick={() => setDesc(!desc)} className="text-xs text-slate-300 hover:text-white">{desc ? "Newest first" : "Oldest first"}</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="mon-history-table" className="w-full text-[11px] text-slate-400">
          <thead><tr className="text-left text-slate-500">{["Run", "Started", "Status", "Funds", "Holdings", "Positions", "Warnings"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {d?.rows.map((r) => (
              <tr key={r.run_id} data-testid={`mon-run-${r.run_id}`}>
                <td className={`${td} font-mono text-slate-200`}>{r.run_id}</td><td className={td}>{utc(r.started_at)}</td>
                <td className={td}><Chip status={r.status} /></td>
                <EpCell e={r.endpoints.funds} /><EpCell e={r.endpoints.holdings} /><EpCell e={r.endpoints.positions} />
                <td className={`${td} text-amber-300`}>{r.warnings.join(", ") || "—"}</td>
              </tr>
            ))}
            {d && !d.rows.length && <tr><td colSpan={7} data-testid="mon-history-empty" className={`${td} text-slate-500`}>No synchronization runs match.</td></tr>}
          </tbody>
        </table>
      </div>
      {d && (
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span data-testid="mon-history-total">{d.total} run(s)</span>
          <div className="flex gap-2"><button data-testid="mon-history-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40">Prev</button><span>{page} / {pages}</span><button data-testid="mon-history-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="disabled:opacity-40">Next</button></div>
        </div>
      )}
    </div>
  );
}
